import { useInfiniteQuery as reactQueryUseInfiniteQuery, } from "@tanstack/react-query";
import { useCallback, useContext, useMemo } from "react";
import invariant from "tiny-invariant";
import { getContext } from "./bootstrap";
import { invalidateQueriesOnce } from "./invalidationDeduper";
import { getConformantId, getDefaultQueryIdentifier, } from "./queryKey";
import { deriveStatus } from "./queryStatus";
import { useEvent } from "./useEvent";
// Trailing discriminator: a `useQuery` and a `useInfiniteQuery` resolving to the same key would
// otherwise share one cache entry holding incompatible shapes (`R` vs `InfiniteData<R, P>`).
const INFINITE_NAMESPACE = "infinite";
// Stable identity for the empty state so consumers can safely memoize on `pages`.
const EMPTY_PAGES = Object.freeze([]);
export const useInfiniteQuery = ({ id, query, initialPageParam, getNextPageParam, invalidation, invalidationDelay, invalidationRefetchType = "all", options = {}, contextId, }) => {
    const { messaging: messagingContext, queryClient: queryClientContext } = getContext({ id: contextId });
    const queryClient = useContext(queryClientContext);
    const { queryBus } = useContext(messagingContext);
    invariant(queryBus, "You are trying to use the useInfiniteQuery hook without wrapping your app with the <MessagingProvider>: there is no queryBus.");
    invariant(queryClient, "You are trying to use the useInfiniteQuery hook without wrapping your app with the <MessagingProvider>: there is no queryClient.");
    // The builder MUST be side-effect free: called here (one throwaway message, with
    // `messageId`/`occurredOn` stripped) to derive a stable key, and again per page in `queryFn`.
    const queryId = id
        ? [...getConformantId(id), INFINITE_NAMESPACE]
        : getDefaultQueryIdentifier(query(initialPageParam), INFINITE_NAMESPACE);
    useEvent({
        callback: () => invalidateQueriesOnce({
            queryClient,
            queryKey: queryId,
            refetchType: invalidationRefetchType,
        }),
        filter: invalidation,
        delay: invalidationDelay,
        contextId,
    });
    const queryFn = useCallback(
    // `pageParam` is typed `unknown` by TanStack pending TPageParam resolution; it is always `P` at runtime.
    async ({ pageParam, signal }) => await queryBus(query(pageParam), signal), [query, queryBus]);
    const { data, status, fetchStatus, fetchNextPage: reactQueryFetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, } = reactQueryUseInfiniteQuery({
        // Spread first on purpose: consumers must not override the hook-owned fields below.
        ...options,
        queryKey: queryId,
        queryFn,
        initialPageParam,
        getNextPageParam,
    }, queryClient);
    const fetchNextPage = useCallback(async () => {
        await reactQueryFetchNextPage();
    }, [reactQueryFetchNextPage]);
    const controls = useMemo(() => ({
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
    }), [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError]);
    return [
        data?.pages ?? EMPTY_PAGES,
        deriveStatus({ status, fetchStatus }),
        controls,
    ];
};
